import { useEffect } from 'react';
import { useReportBuilderStore } from '../../../stores/reportBuilderStore';
import { useUserAccess } from '../../../hooks/useUserAccess';
import { Check, ShieldCheck, Info } from '@phosphor-icons/react';
import {
  SCOPING_LABELS,
  SCOPING_DESCRIPTIONS,
  getAuthorizedScopes,
  getDefaultScope,
} from '../../../utils/reportBuilderScopingUtils';

export default function ScopingStep() {
  const { scopingType, setScopingType } = useReportBuilderStore();
  const access = useUserAccess();

  const authorizedScopes = getAuthorizedScopes(access);

  // Auto-select highest-privilege scope on mount if nothing selected
  useEffect(() => {
    if (!scopingType && !access.isLoading) {
      setScopingType(getDefaultScope(access));
    }
  }, [scopingType, access.isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Data Scope</h2>
        <p className="mt-1 text-sm text-gray-500">
          Choose the scope of data this report should include. This determines which records are visible in results.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {authorizedScopes.map((scope) => {
          const isSelected = scopingType === scope;

          return (
            <button
              key={scope}
              onClick={() => setScopingType(scope)}
              className={`relative rounded-lg border-2 p-4 text-left transition-all ${
                isSelected
                  ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                  : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={18} className={isSelected ? 'text-blue-600' : 'text-gray-400'} />
                  <span className={`text-sm font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                    {SCOPING_LABELS[scope]}
                  </span>
                </div>
                {isSelected && (
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white">
                    <Check size={12} weight="bold" />
                  </div>
                )}
              </div>
              <p className="mt-1.5 text-xs text-gray-500">{SCOPING_DESCRIPTIONS[scope]}</p>
            </button>
          );
        })}
      </div>

      {authorizedScopes.length === 1 && (
        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
          <Info size={16} className="mt-0.5 flex-shrink-0 text-blue-600" />
          <p className="text-xs text-blue-700">
            Your role only has access to one data scope. This has been auto-selected for you.
          </p>
        </div>
      )}
    </div>
  );
}
